<template>
  <div :class="['complex-filter-nested-group', variantClass]">
    <div class="complex-filter-nested-group__rows">
      <div
        v-for="(filterId, index) in filterIds"
        :key="filterId"
        class="complex-filter-nested-group__row"
      >
        <FilterCombinator
          v-if="!hideCombinator"
          :text="index === 0 ? 'Where' : 'And'"
        />
        <ComplexFilterRow
          :model-value="getFilter(filterId)"
          :field-options="getFieldOptionsForRow(filterId)"
          :disabled="disabled"
          :can-delete="canDeleteRow(filterId)"
          :hide-delete="hideDelete"
          :are-filters-read-only="isRowReadOnly(filterId)"
          class="flex-1 min-w-0"
          @update:model-value="(val) => updateFilter(filterId, val)"
          @delete="removeFilter(filterId)"
        />
      </div>
    </div>

    <div
      v-if="
        !disabled &&
        filterMode !== 'readOnly' &&
        (!hideAddFilter || variant === 'nested')
      "
      class="complex-filter-nested-group__actions"
    >
      <BittsButton
        v-if="!hideAddFilter"
        type="neutral"
        text="Add filter"
        @click="addFilter"
        variant="ghost"
        size="small"
        :left-icon="['far', 'plus']"
      />

      <BittsButton
        v-if="variant === 'nested' && !hasRequiredFilter && !hasLockedRow"
        variant="ghost"
        size="x-small"
        type="neutral"
        text="Delete group"
        @click="$emit('delete')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton } from '@crossbeam/bitts';

import { computed, inject, ref } from 'vue';

import { ProvidedUserAddedFilterLabels } from '@/injectionKeys/lists';
import type {
  FilterFieldOption,
  FilterRowState,
  ReportFilterOperatorTypes,
} from '@/types/filters';

import ComplexFilterRow from './ComplexFilterRow.vue';
import FilterCombinator from './FilterCombinator.vue';
import type { FilterMode } from './types';

export type NestedGroupFilter = {
  label: string;
  value?: string | number;
  operator?: ReportFilterOperatorTypes;
  operand?: unknown;
  isLocked?: boolean;
};

const props = withDefaults(
  defineProps<{
    filterIds: string[];
    filters: Map<string, NestedGroupFilter>;
    fieldOptions: FilterFieldOption<string | number>[];
    usedFieldValues?: Set<string | number>;
    variant?: 'root' | 'nested';
    hideAddFilter?: boolean;
    hideCombinator?: boolean;
    hideDelete?: boolean;
    disabled?: boolean;
    filterMode?: FilterMode;
  }>(),
  {
    usedFieldValues: undefined,
    variant: 'nested',
    hideAddFilter: false,
    hideCombinator: false,
    hideDelete: false,
    disabled: false,
    filterMode: 'editable',
  },
);

const emit = defineEmits<{
  'update:filters': [filters: Map<string, NestedGroupFilter>];
  'update:filterIds': [filterIds: string[]];
  delete: [];
}>();

const variantClass = computed(() => {
  return props.variant === 'root' ? 'variant-root' : 'variant-nested';
});

/** When usedFieldValues is provided, exclude already-selected fields from the dropdown (keeping the current row's own selection) */
function getFieldOptionsForRow(
  filterId: string,
): FilterFieldOption<string | number>[] {
  if (!props.usedFieldValues?.size) return props.fieldOptions;

  const currentValue = props.filters.get(filterId)?.value;
  const used = props.usedFieldValues;
  return props.fieldOptions.filter((f) => {
    if (f.value === currentValue) return true;
    return !used?.has(f.value);
  });
}

function canDeleteRow(filterId: string): boolean {
  const filterValue = props.filters.get(filterId)?.value;
  if (filterValue === undefined) return true;
  const field = props.fieldOptions.find((f) => f.value === filterValue);
  return field?.canDelete !== false;
}

function isRowReadOnly(filterId: string): boolean {
  if (props.filterMode === 'readOnly') return true;
  return props.filters.get(filterId)?.isLocked === true;
}

const hasRequiredFilter = computed(() =>
  props.filterIds.some((id) => !canDeleteRow(id)),
);

const hasLockedRow = computed(
  () =>
    props.filterMode === 'hybrid' &&
    props.filterIds.some((id) => props.filters.get(id)?.isLocked === true),
);

function getFilter(id: string): FilterRowState<string | number, unknown> {
  const filter = props.filters.get(id);
  return {
    value: filter?.value,
    operator: filter?.operator,
    operand: filter?.operand,
  };
}

const userAddedFilterLabels = inject(
  ProvidedUserAddedFilterLabels,
  ref(new Set<string>()),
);

function updateFilter(
  id: string,
  val: FilterRowState<string | number, unknown>,
) {
  const newFilters = new Map(props.filters);
  const existing = newFilters.get(id);
  newFilters.set(id, {
    label: existing?.label ?? id,
    value: val.value,
    operator: val.operator,
    operand: val.operand,
    isLocked: existing?.isLocked,
  });
  emit('update:filters', newFilters);
}

function addFilter() {
  const newId = crypto.randomUUID();
  const newFilters = new Map(props.filters);
  newFilters.set(newId, { label: newId });
  if (props.filterMode === 'hybrid') userAddedFilterLabels.value.add(newId);
  emit('update:filters', newFilters);
  emit('update:filterIds', [...props.filterIds, newId]);
}

function removeFilter(id: string) {
  // Only emit filterIds change - ComplexFilterGroup.updateGroupIds
  // will clean up the orphaned filter from the filters Map
  emit(
    'update:filterIds',
    props.filterIds.filter((fid) => fid !== id),
  );
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.complex-filter-nested-group {
  @apply relative min-w-0;
}

.variant-nested {
  @apply rounded-8 p-12 bg-neutral-background;
}

.variant-root {
  @apply bg-transparent p-0;
}

.complex-filter-nested-group__rows {
  @apply flex flex-col gap-8;
}

.complex-filter-nested-group__row {
  @apply flex items-center gap-8 min-w-0;
}

.complex-filter-nested-group__actions {
  @apply flex items-center justify-between mt-12;
}
</style>
